import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import * as moment from 'jalali-moment';
import {Moment} from 'jalali-moment';
import {FormControl} from '@angular/forms';
import {FormValidationService} from '../../services/form-validation/form-validation.service';
import {JalaliDateTimePipe} from '../../pipes/jalali-date-time.pipe';
import {DateUtil} from '../../utils/date.util';
import {Subject} from 'rxjs';

type DateType = 'gregorian' | 'shamsi';
type TimeType = 'am' | 'pm';

export interface IError {
    [context: string]: { message: string };
}

export interface IConfig {
    /**
     * Uses `jalali-moment.js` supported formats. i.e. both YYYY-MM-DD and jYYYY-jMM-jDD are supported.
     */
    format: string;
    /**
     * Can be `outline` for outline based form fields, or none for the plain material.
     */
    appearance: string;
    validation?: {
        [lang: string]: {
            [key: string]: string;
        }
    };
}

@Component({
    selector: 'npa-date-time',
    templateUrl: './npa-date-time.component.html',
    styleUrls: ['./npa-date-time.component.styl']
})
export class NpaDateTimeComponent implements OnInit, OnChanges {
    @Input() label: { date: string; time: string };
    @Input() lang: string;
    @Input() valueChange;
    @Input() required = false as boolean;
    @Input() readonly = false as boolean;
    dateValue: string = undefined;
    timeValue: string = undefined;
    type = 'shamsi' as DateType;
    timeType: TimeType;
    hoursValue: string;
    minutesValue: string;
    yearValue = '';
    dayValue: string;
    monthValue: string;
    @ViewChild('hourInput', {static: false}) hourInput: ElementRef;
    @ViewChild('minuteInput', {static: false}) minuteInput: ElementRef;
    @ViewChild('yearInput', {static: false}) yearInput: ElementRef;
    @ViewChild('monthInput', {static: false}) monthInput: ElementRef;
    @ViewChild('dayInput', {static: false}) dayInput: ElementRef;

    @Input() config: IConfig = {
        format: 'YYYY-MM-DD HH:mm am/pm',
        appearance: 'outline'
    };

    dateValidation: any = {
        format: {
            prs: 'تاریخ باید به فارمت YYYY-MM-DD باشد',
            en: 'Please follow the format YYYY-MM-DD',
            ps: 'نیټه باید په YYYY-MM-DD فارمت وی.'
        },
        required: {
            prs: 'این معلومات الزامی است',
            en: 'This information is required',
            ps: 'دغه مالومات اړین ده'
        },
        value: {
            prs: 'تاریخ وارد شده اشتباه میباشد',
            en: 'The entered date is incorrect',
            ps: 'تاریخ وارد شده اشتباه میباشد'
        }
    };
    timeValidation: any = {
        format: {
            prs: 'زمان باید به فارمت hh:mm باشد',
            en: 'Please follow the format hh:mm',
            ps: 'وخت باید په hh:mm فارت کې وي'
        },
        required: {
            prs: 'این معلومات الزامی است',
            en: 'This information is required',
            ps: 'دغه مالومات اړین ده'
        },
        value: {
            prs: 'زمان وارد شده اشتباه میباشد',
            ps: 'نا سم وخت',
            en: 'Invalid time'
        }
    };

    @Input() model: FormControl;
    @Input() invalidate = new Subject();
    @Output() modelChange: EventEmitter<Date | any> = new EventEmitter<Date | any>();
    error: IError = {time: undefined, date: undefined};

    isDateDirty: boolean;
    isTimeDirty: boolean;

    isDateFocused: boolean;
    isTimeFocused: boolean;

    constructor(
        public formValidationService: FormValidationService,
        private datePipe: JalaliDateTimePipe
    ) {
        this.invalidate.subscribe((options: {shouldInvalidate: boolean, message?: string}) => {
            if (options.shouldInvalidate) {
                this.error = {
                    time: options.message !== undefined ? options.message : this.timeValidation.value[this.lang],
                    date: options.message !== undefined ? options.message : this.dateValidation.value[this.lang]
                };
                this.model.setErrors({invalid: true});
            } else {
                this.error = null;
                this.model.setErrors(null);
            }
        });
    }

    ngOnInit(): void {
        this.changeFormat();
        this.initReadOnlyState();
    }

    setDateInputFocus(element: ElementRef | any) {
        this.isDateFocused = true;
        if (element && element.nativeElement) {
            element.nativeElement.focus();
        } else if (element) {
            element.focus();
        }
    }

    setTimeInputFocus(element: ElementRef | any) {
        this.isTimeFocused = true;
        if (element && element.nativeElement) {
            element.nativeElement.focus();
        } else if (element) {
            element.focus();
        }
    }

    changeFormat() {
        if (this.type === 'shamsi') {
            this.config.format = 'jYYYY-jM-jD H:m am/pm';
        } else {
            this.config.format = 'YYYY-M-D H:m am/pm';
        }
    }

    private initReadOnlyState() {
        this.readonly = this.model.disabled || this.readonly;
    }

    markTimeInputDirty() {
        if (this.hourInput.nativeElement.value !== this.hourInput.nativeElement.defaultValue
            || this.minuteInput.nativeElement.value !== this.minuteInput.nativeElement.defaultValue) {
            this.isTimeDirty = true;
        }
    }

    markDateInputDirty() {
        if (this.yearInput.nativeElement.value !== this.yearInput.nativeElement.defaultValue
            || this.monthInput.nativeElement.value !== this.monthInput.nativeElement.defaultValue
            || this.dayInput.nativeElement.value !== this.dayInput.nativeElement.defaultValue
        ) {
            this.isDateDirty = true;
        }
    }

    dayInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Tab' || $event.key === ':') {
            this.dayInput.nativeElement.focus();
        }
        if ($event.key === 'Backspace') {
            this.dayValue = this.dayValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const dateType = this.type;
        const day = $event.key;
        let daysInMonth;
        const INITIAL_DIGIT = '0';
        if (dateType === 'shamsi') {
            daysInMonth = moment(this.yearValue + '-' + this.monthValue, 'jYYYY-jM').daysInMonth();
        } else {
            daysInMonth = moment(this.yearValue + '-' + this.monthValue, 'YYYY-MM').daysInMonth();
        }
        if (this.dayValue && this.dayValue.length > 1) {
            this.dayValue = this.dayValue.slice(1) + day;
        } else {
            this.dayValue = INITIAL_DIGIT + day;
        }
        if (+this.dayValue > daysInMonth) {
            this.error.date = {message: this.dateValidation.value[this.lang]};
        } else {
            this.error.date = undefined;
        }
        this.updateDateValue();
        this.markDateInputDirty();
        return false;
    }

    monthInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Tab' || $event.key === ':') {
            this.monthInput.nativeElement.focus();
        }
        if ($event.key === 'Backspace') {
            this.monthValue = this.monthValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const INITIAL_DIGIT = '0';
        const month = $event.key;
        if (this.monthValue && this.monthValue.length > 1) {
            this.monthValue = this.monthValue.slice(1) + month;
        } else {
            this.monthValue = INITIAL_DIGIT + month;
        }
        this.updateDateValue();
        this.markDateInputDirty();
        return false;
    }

    yearInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Tab' || $event.key === ':') {
            this.yearInput.nativeElement.focus();
        }
        if ($event.key === 'Backspace') {
            this.yearValue = this.yearValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const year = $event.key;
        if (this.yearValue && this.yearValue.length >= 4) {
            this.yearValue = this.yearValue.slice(1) + year;
        } else {
            this.yearValue += year;
        }
        this.updateDateValue();
        this.markDateInputDirty();
        return false;
    }

    hourInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Tab' || $event.key === ':') {
            this.hourInput.nativeElement.focus();
        }
        if ($event.key === 'Backspace') {
            this.hoursValue = this.hoursValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const hasManuallyEnteredZero = $event.key === '0';

        const INITIAL_DIGIT = '0';
        const hours = $event.key;
        if (this.hoursValue && this.hoursValue.length > 1) {
            this.hoursValue = this.hoursValue.slice(1) + hours;
        } else {
            this.hoursValue = INITIAL_DIGIT + hours;
        }
        this.timeType = +this.hoursValue > 12 && this.timeType === 'am' ?
            'pm' : (+this.hoursValue === 12 ? this.timeType : 'am');
        // if (this.hoursValue.length === 2 && hasManuallyEnteredZero) {
        //     this.minuteInput.nativeElement.focus();
        // }
        this.markTimeInputDirty();
        return false;
    }

    minuteInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Backspace') {
            this.minutesValue = this.minutesValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const INITIAL_DIGIT = '0';
        const minutes = $event.key;
        if (this.minutesValue && this.minutesValue.length > 1) {
            this.minutesValue = this.minutesValue.slice(1) + minutes;
        } else {
            this.minutesValue = INITIAL_DIGIT + minutes;
        }
        this.markTimeInputDirty();
        return false;
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.timeType = this.timeType === undefined ? 'am' : this.timeType;

        this.error = {date: this.error.date, time: this.error.time};
        if (this.model && (this.model.value === undefined || this.model.value === '')) {
            return;
        }
        if (changes.valueChange && changes.valueChange.currentValue) {
            const enteredDate = changes.valueChange.currentValue;
            const date = new Date(enteredDate);
            const dateMoment = moment(DateUtil.convert(date));
            const timeType = dateMoment.locale('en').format('hh:mm a').split(' ')[1] as TimeType;
            this.timeType = timeType;
            this.setTime(dateMoment, timeType);
            if (this.type === 'shamsi') {
                dateMoment.format('jYYYY-jMM-jDD');
                this.setDate(dateMoment, this.type);
                this.updateDateValue();
            } else {
                dateMoment.format('YYYY-MM-DD');
                this.setDate(dateMoment, this.type);
                this.updateDateValue();
            }
        }
    }

    onTimeChange() {
        const timeValue = this.hoursValue + ':' + this.minutesValue;
        if (!this.isTimeValid(timeValue)) {
            console.error('time format is INVALID', timeValue);
            this.error.time = {message: this.timeValidation.value[this.lang]};
            return;
        }
        if (+this.hoursValue > 24 || +this.minutesValue > 59) {
            this.error.time = {message: this.timeValidation.value[this.lang]};
            console.error('time is INVALID', timeValue);
            return;
        }
        this.error.time = undefined;
        if (timeValue && this.isTimeValid(timeValue)) {
            this.timeValue = timeValue;
            const dateTime = this.getDate() + ' ' + this.changeTo24hours(timeValue);
            this.model.setValue(dateTime);
            this.modelChange.emit(this.model);
        } else {
            this.model.setValue(undefined);
            this.modelChange.emit(this.model);
        }
    }

    onDateChange() {
        const dateValue = this.dateValue;
        try {
            if ((this.type === 'shamsi' && (+this.yearValue >= 1500 || +this.yearValue <= 1300))
                || (this.type === 'gregorian' && (+this.yearValue >= 3000 || +this.yearValue <= 1900))
                || +this.monthValue > 12
                || +this.dayValue > 31
            ) {
                this.error.date = {message: this.dateValidation.value[this.lang]};
                return;
            }
            const dateMoment = moment(dateValue, this.config.format);
            if (!this.isDateValid(dateValue)) {
                this.error.date = {message: this.dateValidation.format[this.lang]};
                return;
            }
            if (!dateMoment.isValid()) {
                this.error.date = {message: this.dateValidation.value[this.lang]};
                return;
            }
            this.error.date = undefined;
            if (dateMoment.isValid() && dateValue.length === 10) {
                const gregDate = moment(DateUtil.convert(dateMoment.toDate())).format('YYYY-MM-DD');
                const dateTime = gregDate + ' ' + this.changeTo24hours(this.timeValue);
                this.model.setValue(dateTime);
                this.modelChange.emit(this.model);
            } else {
                this.model.setValue(undefined);
                this.modelChange.emit(this.model);
            }
        } catch (error) {
            console.error(error);
        }
    }

    private isDateValid(value: string) {
        return /^(19|20|13|14)\d\d([-])(0[1-9]|[1-9]|1[012])([-])(0[1-9]|[1-9]|[12][0-9]|3[01])$/i.test(value);
    }

    changeDateType(typeSlug: DateType) {
        if (typeSlug === this.type) {
            return;
        }
        this.type = typeSlug;
        if (this.dateValue) {
            if (this.type === 'shamsi') {
                const dateMoment = moment(DateUtil.convert((new Date(this.dateValue))));
                this.dateValue = moment(DateUtil.convert((new Date(this.dateValue)))).format('jYYYY-jMM-jDD');
                this.setDate(dateMoment, this.type);
                this.updateDateValue();
            } else {
                const dateMoment = moment(this.dateValue, 'jYYYY-jMM-jDD');
                this.dateValue = moment(DateUtil.convert(dateMoment.toDate())).format('YYYY-MM-DD');
                const d = moment(DateUtil.convert(dateMoment.toDate()));
                this.setDate(d, this.type);
                this.updateDateValue();
            }
        }
        this.changeFormat();
    }

    changeTimeType(typeSlug: TimeType) {
        this.timeType = typeSlug;
        this.onTimeChange();
    }

    isTimeValid(value: string): boolean {
        return /^([1-9]|0[1-9]|1[012])([:])([0-9]|0[0-9]|[1-5][0-9])$/i.test(value);
    }

    adjustHours() {
        if (+this.hoursValue > 12 && +this.hoursValue < 24) {
            const offsetHours = +this.hoursValue - 12;
            this.hoursValue = (offsetHours < 10 ? '0' + offsetHours : offsetHours).toString();
        }
    }

    changeTo24hours(time: string) {
        const timeArray = time.split(':');
        const hours = +timeArray[0];
        const minutes = +timeArray[1];
        let timeString = time;
        if (this.timeType === 'pm') {
            if (hours === 12) {
                timeString = '12:' + (minutes < 10 ? '0' + minutes : minutes);
            } else {
                timeString = '' + (hours > 12 ? hours : hours + 12) + ':' + (minutes < 10 ? '0' + minutes : minutes);
            }
        } else if (this.timeType === 'am') {
            if (hours === 12) {
                timeString = '00:' + (minutes < 10 ? '0' + minutes : minutes);
            } else if (hours < 12) {
                timeString = (hours < 10 ? '0' + hours : hours) + ':' + (minutes < 10 ? '0' + minutes : minutes);
            }
        }
        return timeString;
    }

    updateDateValue() {
        this.dateValue = this.yearValue + '-' + this.monthValue + '-' + this.dayValue;
    }

    setDate(dateMoment: Moment, type: DateType) {
        const year = (type === 'shamsi' ? dateMoment.jYear() : dateMoment.year());
        const month = ((type === 'shamsi' ? dateMoment.jMonth() : dateMoment.month()) + 1);
        const day = (type === 'shamsi' ? dateMoment.jDate() : dateMoment.date());
        this.yearValue = year.toString();
        this.monthValue = (month < 10 ? '0' + month : month).toString();
        this.dayValue = (day < 10 ? '0' + day : day).toString();
    }

    setTime(dateMoment: Moment, type: TimeType) {
        let hours = dateMoment.hours();
        const minutes = dateMoment.minutes();
        if (hours > 12) {
            hours -= 12;
        }
        this.hoursValue = (hours < 10 ? '0' + hours : hours).toString();
        this.minutesValue = (minutes < 10 ? '0' + minutes : minutes).toString();
        this.timeValue = this.hoursValue + ':' + this.minutesValue;
    }

    getDateTZConvert() {
        return this.type === 'shamsi' ?
            this.dateValue = moment(DateUtil.convert((moment(this.dateValue, 'jYYYY-jMM-jDD'))
                .toDate())).format('YYYY-MM-DD') :
            this.dateValue;
    }

    getDate() {
        return this.type === 'shamsi' ?
            moment(moment(this.dateValue, 'jYYYY-jMM-jDD')
                .toDate()).format('YYYY-MM-DD') :
            this.dateValue;
    }
}
